import React from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Breadcrumbs } from "@/components/layout/Breadcrumbs";
import { ARTICLES, ARTICLE_CATEGORIES, getArticleBySlug } from "@/data/articles";
import { useLocalize } from "@/data/localize";
import { SEO } from "@/lib/seo";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Search, Clock, ArrowRight, Calendar } from "lucide-react";

export function EducationIndex() {
    const { t } = useTranslation();
    const { localizeList } = useLocalize();
    const articles = localizeList(ARTICLES);

    const [query, setQuery] = React.useState("");
    const [category, setCategory] = React.useState("All");

    const filtered = articles.filter((a) => {
        const raw = ARTICLES.find((x) => x.slug === a.slug);
        const rawCat = raw ? raw.category : a.category;
        const matchesCat = category === "All" || rawCat === category;
        const q = query.trim().toLowerCase();
        const matchesQ =
            !q || (a.title || "").toLowerCase().includes(q) || (a.excerpt || "").toLowerCase().includes(q);
        return matchesCat && matchesQ;
    });

    const featured = filtered[0];
    const rest = filtered.slice(1);

    return (
        <>
            <SEO title={t("education.seoTitle")} description={t("education.seoDescription")} />
            <div className="container-page pt-8">
                <Breadcrumbs items={[{ label: t("education.crumb") }]} />
            </div>
            <section className="container-page pt-6 pb-4">
                <div className="max-w-3xl">
                    <span className="chip-teal">{t("education.chip")}</span>
                    <h1 className="mt-3 font-display text-4xl sm:text-5xl font-semibold text-[color:var(--brand-navy)] leading-tight">
                        {t("education.title")}
                    </h1>
                    <p className="mt-4 text-[color:var(--text-muted)] leading-relaxed text-lg">
                        {t("education.intro")}
                    </p>
                </div>
            </section>
            <section className="container-page pb-6">
                <div className="card-soft p-4 sm:p-5 flex flex-col md:flex-row gap-3 md:items-center">
                    <div className="relative flex-1">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[color:var(--text-soft)]" />
                        <Input
                            data-testid="education-search-input"
                            value={query}
                            onChange={(e) => setQuery(e.target.value)}
                            placeholder={t("education.searchPlaceholder")}
                            className="pl-9 h-11 rounded-xl"
                        />
                    </div>
                    <div className="flex flex-wrap gap-2">
                        {ARTICLE_CATEGORIES.map((c) => (
                            <button
                                key={c}
                                onClick={() => setCategory(c)}
                                data-testid={`education-filter-${c.toLowerCase()}`}
                                className={`rounded-full px-3.5 py-1.5 text-xs font-medium transition-colors ${
                                    category === c
                                        ? "bg-[color:var(--brand-navy)] text-white"
                                        : "bg-[color:var(--bg-subtle)] text-[color:var(--text-muted)] hover:bg-[color:var(--brand-teal-soft)] hover:text-[color:var(--brand-teal-2)]"
                                }`}
                            >
                                {t(`education.categories.${c}`, c)}
                            </button>
                        ))}
                    </div>
                </div>
            </section>

            <section className="container-page pb-6" data-testid="education-article-grid">
                {filtered.length === 0 ? (
                    <div className="card-soft p-10 text-center text-[color:var(--text-muted)]">
                        {t("education.emptyState")}
                    </div>
                ) : (
                    <div className="grid gap-5 md:grid-cols-3">
                        {featured && (
                            <Link
                                to={`/education/${featured.slug}`}
                                data-testid="education-featured-article"
                                className="card-soft p-6 md:col-span-2 group flex flex-col justify-between"
                                style={{ background: "linear-gradient(135deg, rgba(14,124,134,0.10) 0%, rgba(255,255,255,1) 55%)" }}
                            >
                                <div>
                                    <Badge className="bg-[color:var(--brand-teal-soft)] text-[color:var(--brand-teal-2)] hover:bg-[color:var(--brand-teal-soft)]">{t("education.featured")}</Badge>
                                    <h2 className="mt-3 font-display text-2xl sm:text-3xl font-semibold text-[color:var(--brand-navy)] leading-tight">
                                        {featured.title}
                                    </h2>
                                    <p className="mt-3 text-[color:var(--text-muted)] leading-relaxed">{featured.excerpt}</p>
                                </div>
                                <ArticleMeta a={featured} showArrow />
                            </Link>
                        )}
                        {rest.map((a) => (
                            <Link
                                key={a.slug}
                                to={`/education/${a.slug}`}
                                data-testid={`education-article-card-${a.slug}`}
                                className="card-soft p-6 group flex flex-col justify-between"
                            >
                                <div>
                                    <Badge variant="secondary" className="bg-[color:var(--bg-subtle)] text-[color:var(--text-soft)]">{a.category}</Badge>
                                    <h3 className="mt-3 font-display text-lg font-semibold text-[color:var(--brand-navy)]">{a.title}</h3>
                                    <p className="mt-2 text-sm text-[color:var(--text-muted)] leading-relaxed line-clamp-3">{a.excerpt}</p>
                                </div>
                                <ArticleMeta a={a} />
                            </Link>
                        ))}
                    </div>
                )}
            </section>
        </>
    );
}

function ArticleMeta({ a, showArrow = false }) {
    const { t } = useTranslation();
    return (
        <div className="mt-5 flex items-center gap-4 text-xs text-[color:var(--text-soft)]">
            <span className="inline-flex items-center gap-1"><Clock className="h-3.5 w-3.5" />{t("education.minRead", { count: a.read_time })}</span>
            <span className="inline-flex items-center gap-1"><Calendar className="h-3.5 w-3.5" />{a.updated}</span>
            {showArrow && (
                <span className="ml-auto inline-flex items-center gap-1 text-[color:var(--brand-teal-2)] font-semibold">
                    {t("education.read")}
                </span>
            )}
        </div>
    );
}

export function ArticleDetail() {
    const { t } = useTranslation();
    const { localize } = useLocalize();
    const { slug } = useParams();
    const raw = getArticleBySlug(slug);
    if (!raw) return <Navigate to="/education" replace />;
    const a = localize(raw);

    return (
        <>
            <SEO title={a.title} description={a.excerpt} />
            <div className="container-page pt-8">
                <Breadcrumbs items={[{ to: "/education", label: t("education.crumb") }, { label: a.title }]} />
            </div>
            <section className="container-page pt-6 pb-8">
                <div className="max-w-3xl">
                    <Badge className="bg-[color:var(--brand-teal-soft)] text-[color:var(--brand-teal-2)] hover:bg-[color:var(--brand-teal-soft)]">{a.category}</Badge>
                    <h1 className="mt-3 font-display text-4xl sm:text-5xl font-semibold text-[color:var(--brand-navy)] leading-tight">{a.title}</h1>
                    <div className="mt-3 flex items-center gap-4 text-sm text-[color:var(--text-soft)]">
                        <span className="inline-flex items-center gap-1"><Clock className="h-4 w-4" />{t("education.minRead", { count: a.read_time })}</span>
                        <span className="inline-flex items-center gap-1"><Calendar className="h-4 w-4" />{t("education.updated", { date: a.updated })}</span>
                    </div>
                    <p className="mt-5 text-lg text-[color:var(--text-muted)] leading-relaxed">{a.excerpt}</p>

                    <article className="prose-clinical mt-8">
                        {(a.body || []).map((block, i) => {
                            if (block.type === "h2") return <h2 key={i}>{block.text}</h2>;
                            if (block.type === "h3") return <h3 key={i}>{block.text}</h3>;
                            if (block.type === "p") return <p key={i}>{block.text}</p>;
                            if (block.type === "ul")
                                return <ul key={i}>{block.items.map((x) => <li key={x}>{x}</li>)}</ul>;
                            return null;
                        })}
                    </article>

                    <p className="mt-8 text-xs text-[color:var(--text-soft)] italic">
                        {t("education.educationalDisclaimer")}
                    </p>
                </div>
            </section>
        </>
    );
}
